import React from "react"
import * as Chakra from "@chakra-ui/react"
import { useKeenSlider } from "keen-slider/react"
import {
  AnimationState,
  ScrollFadeIn,
  useAnimatableLayoutEffect,
} from "../animation-helpers/animation-state"
import useMouse from "@react-hook/mouse-position"
import {ResponsiveVideoImage} from "../video-image";

interface ProjectSliderProps {
  quote?: string
  attribution?: string
  images: { image: any, video_url?: string }[]
  sliderDirection: "row" | "row-reverse" | "center-wide" | "center-portrait"
}

const ProjectSlider = React.forwardRef((props: ProjectSliderProps, ref) => {
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const [sliderRef, slider] = useKeenSlider({
    slidesPerView: 1,
    initial: 0,
    loop: true,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide)
    },
  })

  const [withMouse, setWithMouse] = React.useState(false)

  const mouseRef = React.useRef(null)

  const mouse = useMouse(mouseRef, {
    enterDelay: 100,
    leaveDelay: 0,
  })

  const [width, setWidth] = React.useState(0)
  const [leftOffset, setLeftOffset] = React.useState(0)

  useAnimatableLayoutEffect(() => {
    if (mouseRef?.current) {
      const { width, left } = AnimationState.getPositionOfElement(mouseRef)
      setWidth(width)
      setLeftOffset(left)
    }
  }, [mouseRef])

  return (
    <Chakra.Box
      width="100%"
      display="flex"
      position="relative"
      alignItems="flex-start"
      flexDirection="column"
      backgroundColor="transparent"
      ref={ref}
    >
      <Chakra.Box
        width="100%"
        display="flex"
        maxWidth="1800px"
        alignItems="flex-start"
        marginLeft="auto"
        marginRight="auto"
        paddingLeft="40px"
        paddingRight="40px"
        flexDirection={props.sliderDirection === "row-reverse" ? "row-reverse" : "row"}
        justifyContent="space-between"
        backgroundColor="transparent"
        className="project-block"
        as={ScrollFadeIn}
        css={{
          "@media (max-width: 991px)": {
            paddingLeft: "20px",
            paddingRight: "20px",
            flexDirection: "column",
          },
        }}
      >
        {(props.sliderDirection === "row" ||
          props.sliderDirection === "row-reverse") && (
          <Chakra.Box
            width="100%"
            display="flex"
            alignItems="flex-start"
            paddingRight={{base: "0", xl:`${
              props.sliderDirection && props.sliderDirection === "row"
                ? "31px"
                : "0"}`
            }}
            paddingLeft={{base: "0", xl:`${
              props.sliderDirection && props.sliderDirection === "row-reverse"
                ? "31px"
                : "0"}`
            }}
            flexDirection="column"
            justifyContent="flex-start"
            backgroundColor="transparent"
            css={{
              "@media (max-width: 991px)": {
                paddingLeft: 0,
                paddingRight: "0px",
                paddingBottom: "40px",
              },
            }}
          >
            <Chakra.Box
              fontSize="36px"
              maxWidth="calc(50% - 15px)"
              fontStyle="normal"
              fontFamily="Beausite 400"
              fontWeight="400"
              lineHeight="43px"
              paddingBottom="18px"
              css={{
                "@media (max-width: 991px)": {
                  maxWidth: "100%",
                },
                "@media (max-width: 767px)": {
                  fontFamily: "BeausiteClassic-Clear",
                },
                "@media (max-width: 479px)": {
                  fontSize: "28px",
                },
              }}
            >
              {props.quote}
            </Chakra.Box>
            <Chakra.Box
              as="span"
              fontSize="18px"
              fontStyle="normal"
              fontFamily="Beausite 400"
              fontWeight="400"
              lineHeight="26px"
              css={{
                "@media (max-width: 767px)": {
                  fontFamily: "BeausiteClassic-Clear",
                },
              }}
            >
              {props.attribution}
            </Chakra.Box>
          </Chakra.Box>
        )}

        <Chakra.Flex
          width="100%"
          flexShrink={0}
          // maxWidth={props.sliderDirection === "center-wide" ? "100%" : "50%"}
          maxWidth={{base: "100%", xl: props.sliderDirection === "center-wide" ? "100%" : props.sliderDirection === "center-portrait" ? "50%" : "calc(50% - 15px)"}}
          mx={props.sliderDirection === "center-portrait" ? "auto" : undefined}
          // minHeight="844px"
          flexDirection="column"
          height="100%"
          alignItems="flex-end"
          ref={mouseRef}
          onMouseOver={() => setWithMouse(true)}
          onMouseOut={() => setWithMouse(false)}
          cursor={props?.images?.length > 1 ? "none" : ""}
          position="relative"
          onClick={() => {
            if (mouse.pageX - leftOffset < width * 0.5) {
              slider.prev()
            } else if (mouse.pageX - leftOffset > width * 0.5) {
              slider.next()
            }
          }}
        >
          {props?.images?.length > 1 && (
            <Chakra.Box
              position="absolute"
              transition="0.05s"
              style={{
                left: `${mouse.x}px`,
                top: `${mouse.y}px`,
                opacity: withMouse ? 1 : 0,
              }}
              zIndex="10"
              color="#fff"
              fontSize="30px"
            >
              {mouse.pageX - leftOffset < width * 0.5 && "⟵"}
              {mouse.pageX - leftOffset > width * 0.5 && "⟶"}
            </Chakra.Box>
          )}
          <Chakra.Box
            ref={sliderRef}
            className="keen-slider"
            width="100%"
            maxWidth="100%"
            minHeight="844px"
            height="100%"
            onMouseOver={() => setWithMouse(true)}
            onMouseOut={() => setWithMouse(false)}
          >
            {props?.images &&
              props.images.map(({ image, video_url }, index) => (
                <Chakra.Box className="keen-slider__slide" key={index}>
                  <ResponsiveVideoImage
                      video_url={video_url}
                    image={image}
                    width="100%"
                    maxWidth="100%"
                    minHeight="844px"
                    height="100%"
                    objectFit="cover"
                    css={{
                      "@media (max-width: 991px)": {
                        maxWidth: "100%",
                      },
                    }}
                  />
                </Chakra.Box>
              ))}
          </Chakra.Box>
          <Chakra.Box>
            {slider && slider.details && slider.details().size > 1 && (
              <Chakra.Box
                as="div"
                paddingTop="30px"
                textAlign="right"
                fontFamily="Beausite 400"
                lineHeight="24px"
                css={{
                  "@media (max-width: 767px)": {
                    fontFamily: "BeausiteClassic-Clear",
                  },
                }}
              >
                {currentSlide + 1}/
                {slider && slider.details && slider.details().size}
              </Chakra.Box>
            )}
          </Chakra.Box>
        </Chakra.Flex>
      </Chakra.Box>
    </Chakra.Box>
  )
})

ProjectSlider.displayName = "ProjectSlider"

ProjectSlider.pressless = {
  name: "Project Slider",
  attributes: {
    images: {
      name: "Slider Images",
      type: "Repeater",
      attributes: {
        image: {
          name: "Image",
          type: "Image",
        },
        video_url: {
          name: "Video (URL)",
          type: "PlainText"
        }
      },
    },
    quote: {
      name: "Quote",
      type: "RichText",
    },
    attribution: {
      name: "Quote Attribution",
      type: "RichText",
    },
    sliderDirection: {
      name: "Slider Direction",
      type: "Select",
      options: [
        { row: "Left-to-Right" },
        { "row-reverse": "Right-to-Left" },
        { "center-wide": "Center-Wide" },
        { "center-portrait": "Center-Portrait" },
      ],
    },
  },
}

export default ProjectSlider
